import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
    pointerWithin,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
    Plus, Search, TrendingUp, Users, Mail as MailIcon, PhoneCall,
    Calendar, Trophy, XCircle, Loader2, GripVertical, Phone, MailCheck,
    Clock, Send, Snowflake, ThermometerSun, ArrowRightLeft,
} from "lucide-react";
import * as api from "../../lib/crmApi";
import { sanitizeHtml } from "../../lib/sanitize";
import CrmLeadDrawer from "./CrmLeadDrawer";
import ExportMenu from "./ExportMenu";
import CrmCreateLeadModal from "./CrmCreateLeadModal";
import TemplatePicker from "./TemplatePicker";

const fmtDkk = (v) => new Intl.NumberFormat("da-DK").format(v || 0) + " kr.";
const fmtDwell = (h) => {
    if (!h || h < 1) return "<1t";
    if (h < 24) return `${Math.round(h)}t`;
    return `${(h / 24).toFixed(1)}d`;
};

// How long has the lead been in the current pipeline stage? Falls back to
// `updated_at`/`created_at` for pre-migration leads that lack stage_since.
// Returns `{ label, tier, days }` — tier is 'neutral' | 'warm' | 'cold'.
// A lead is only cold-flagged if it's still active (no reply, no meeting,
// not won/lost) — terminal stages naturally sit for a long time and
// shouldn't nag the rep.
function getDwellInfo(lead) {
    const since = lead.stage_since || lead.updated_at || lead.created_at;
    if (!since) return null;
    let days = 0;
    let label = null;
    try {
        const ms = Date.now() - new Date(since).getTime();
        const mins = Math.floor(ms / 60000);
        if (mins < 60) label = `${mins}m`;
        else {
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) label = `${hrs}t`;
            else {
                days = Math.floor(hrs / 24);
                if (days < 30) label = `${days}d`;
                else label = `${Math.floor(days / 30)}mdr`;
            }
        }
    } catch { return null; }

    const isTerminal = lead.replied_at || lead.won_value != null
        || lead.lost_reason || lead.meeting;
    let tier = "neutral";
    if (!isTerminal) {
        if (days >= 14) tier = "cold";
        else if (days >= 7) tier = "warm";
    }
    return { label, tier, days };
}

// Utility: mix a hex color with white to a lighter tint (for column bg).
function tint(hex, amount = 0.9) {
    if (!hex) return "#f1f5f9";
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const mix = (c) => Math.round(c + (255 - c) * amount);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

export default function CrmPipeline() {
    const [leads, setLeads] = useState([]);
    const [stages, setStages] = useState([]); // full stage docs
    const [metrics, setMetrics] = useState({}); // keyed by stage.name
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [openLead, setOpenLead] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [dragging, setDragging] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
    }));

    const [templates, setTemplates] = useState([]);
    // Bulk selection state — a Set of lead ids currently ticked.
    // Kept as a plain object-array so React re-renders on mutation.
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkModal, setBulkModal] = useState(null); // "send" | null

    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            if (checked) return prev.includes(id) ? prev : [...prev, id];
            return prev.filter((x) => x !== id);
        });
    };
    const clearSelection = () => setSelectedIds([]);

    const refresh = async () => {
        setLoading(true);
        try {
            const [lds, sts, stg, met, tpl] = await Promise.all([
                api.listLeads({ q: query || undefined }),
                api.getStats(),
                api.listStagesManage(),
                api.listStagesMetrics(),
                api.listTemplates().catch(() => []),
            ]);
            setLeads(lds);
            setStats(sts);
            setStages(stg);
            setMetrics(Object.fromEntries(met.map((r) => [r.name, r])));
            setTemplates(tpl);
        } catch (e) {
            toast.error("Kunne ikke hente leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []); // eslint-disable-line

    const leadsByStage = useMemo(() => {
        const map = Object.fromEntries(stages.map((s) => [s.name, []]));
        (leads || []).forEach((l) => {
            if (map[l.stage]) map[l.stage].push(l);
        });
        return map;
    }, [leads, stages]);

    const handleDragEnd = async (event) => {
        setDragging(null);
        const { active, over } = event;
        if (!over || !active) return;
        const leadId = active.id;
        const newStage = over.id;
        const lead = leads.find((l) => l.id === leadId);
        if (!lead || lead.stage === newStage) return;
        // Optimistic update
        setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, stage: newStage } : l));
        try {
            await api.moveStage(leadId, newStage);
            toast.success(`Flyttet til "${newStage}"`);
            refresh();
        } catch (e) {
            toast.error("Kunne ikke flytte lead");
            refresh();
        }
    };

    const activeDragLead = dragging ? leads.find((l) => l.id === dragging) : null;

    // Count leads that have gone cold (>=14 days in current stage AND not
    // in a terminal state). Shown as a KPI card.
    const coldCount = useMemo(() => {
        return (leads || []).reduce((n, l) => {
            const info = getDwellInfo(l);
            return n + (info?.tier === "cold" ? 1 : 0);
        }, 0);
    }, [leads]);

    const sendTemplateFromCard = async (lead, template) => {
        if (!window.confirm(
            `Send "${template.name}" til ${lead.email}?`
        )) return;
        try {
            await api.sendTemplateToLead(lead.id, template.id);
            toast.success(`Mail sendt til ${lead.company}`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke sende");
        }
    };

    const bulkMove = async (stage) => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(
            `Flyt ${selectedIds.length} lead${selectedIds.length === 1 ? "" : "s"} til "${stage}"?`
        )) return;
        try {
            const r = await api.bulkMoveStage(selectedIds, stage);
            toast.success(
                `${r.moved} flyttet til ${stage}` +
                (r.skipped ? ` · ${r.skipped} sprunget over` : "")
            );
            clearSelection();
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke flytte");
        }
    };

    const bulkSend = async (template) => {
        if (selectedIds.length === 0) return;
        try {
            const r = await api.bulkSendTemplate(selectedIds, template.id);
            const parts = [`${r.sent} sendt`];
            if (r.skipped_no_consent) parts.push(`${r.skipped_no_consent} uden samtykke`);
            if (r.skipped_unsubscribed) parts.push(`${r.skipped_unsubscribed} afmeldt`);
            if (r.skipped_rate_limited) parts.push(`${r.skipped_rate_limited} rate-limit`);
            if (r.errors?.length) parts.push(`${r.errors.length} fejl`);
            toast.success(parts.join(" · "));
            setBulkModal(null);
            clearSelection();
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke sende");
        }
    };

    return (
        <div className="p-6 lg:p-8 h-screen flex flex-col overflow-hidden" data-testid="crm-pipeline">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h1 className="font-display text-3xl font-bold text-slate-900">Salgspipeline</h1>
                    <p className="text-sm text-slate-500 mt-1">Træk kort mellem kolonner for at flytte leads</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && refresh()}
                            placeholder="Søg virksomhed, navn, email..."
                            data-testid="crm-search"
                            className="pl-9 pr-4 h-10 w-64 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowCreate(true)}
                        data-testid="crm-create-lead"
                        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10 shadow-sm"
                    >
                        <Plus className="h-4 w-4" /> Opret nyt lead
                    </button>
                </div>
            </div>

            {/* KPI cards */}
            {stats && <KpiRow stats={stats} coldCount={coldCount} />}

            {/* Kanban board */}
            {loading ? (
                <div className="flex items-center justify-center py-24 text-slate-400">
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={(e) => setDragging(e.active.id)}
                    onDragEnd={handleDragEnd}
                    onDragCancel={() => setDragging(null)}
                >
                    {/* Kanban viewport — takes ALL remaining vertical
                        space (flex-1 min-h-0 inside the h-screen parent)
                        so the horizontal scrollbar always sits at the
                        bottom of the visible viewport, no matter how
                        many leads are loaded. Vertical scroll happens
                        INSIDE the pane instead of on the page. */}
                    <div
                        className="flex-1 min-h-0 overflow-auto pb-2 rounded-lg"
                        data-testid="crm-kanban">
                        <div className="flex gap-4 min-w-max">
                            {stages.map((stage) => (
                                <StageColumn
                                    key={stage.id}
                                    stage={stage}
                                    metrics={metrics[stage.name]}
                                    leads={leadsByStage[stage.name] || []}
                                    onOpenLead={setOpenLead}
                                    onQuickAdd={() => setShowCreate(true)}
                                    templates={templates}
                                    onSendTemplate={sendTemplateFromCard}
                                    selectedIds={selectedIds}
                                    onToggleSelect={toggleSelect}
                                />
                            ))}
                        </div>
                    </div>
                    <DragOverlay>
                        {activeDragLead && <LeadCard lead={activeDragLead} dragging />}
                    </DragOverlay>
                </DndContext>
            )}

            {showCreate && (
                <CrmCreateLeadModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); refresh(); }}
                />
            )}
            {openLead && (
                <CrmLeadDrawer
                    leadId={openLead}
                    onClose={() => setOpenLead(null)}
                    onChanged={refresh}
                />
            )}

            {/* Bulk action bar — appears when at least one lead is ticked.
                Fixed-bottom so it stays visible while the rep scrolls. */}
            {selectedIds.length > 0 && (
                <BulkActionBar
                    count={selectedIds.length}
                    selectedIds={selectedIds}
                    stages={stages}
                    canSend={templates.length > 0}
                    onMove={bulkMove}
                    onSendClick={() => setBulkModal("send")}
                    onClear={clearSelection}
                />
            )}
            {bulkModal === "send" && (
                <BulkSendModal
                    count={selectedIds.length}
                    templates={templates}
                    onClose={() => setBulkModal(null)}
                    onConfirm={bulkSend}
                />
            )}
        </div>
    );
}

function BulkActionBar({ count, stages, canSend, selectedIds, onMove, onSendClick, onClear }) {
    const [moveOpen, setMoveOpen] = useState(false);
    return (
        <div
            data-testid="crm-bulk-bar"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 min-w-[420px]"
        >
            <div className="text-sm font-semibold">
                <span className="tabular-nums">{count}</span> lead{count === 1 ? "" : "s"} valgt
            </div>
            <div className="h-6 w-px bg-white/20" />
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setMoveOpen((v) => !v)}
                    data-testid="crm-bulk-move"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 h-9 text-xs font-semibold">
                    <ArrowRightLeft className="h-3.5 w-3.5" /> Flyt til…
                </button>
                {moveOpen && (
                    <div
                        onMouseLeave={() => setMoveOpen(false)}
                        className="absolute bottom-11 left-0 bg-white text-slate-900 rounded-lg shadow-xl border border-slate-200 py-1 min-w-[220px] max-h-72 overflow-y-auto">
                        {stages.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => { setMoveOpen(false); onMove(s.name); }}
                                data-testid={`crm-bulk-move-to-${s.name}`}
                                className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm flex items-center gap-2">
                                <span
                                    aria-hidden="true"
                                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                    style={{ background: s.color || "#94a3b8" }}
                                />
                                {s.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <button
                type="button"
                onClick={onSendClick}
                disabled={!canSend}
                data-testid="crm-bulk-send"
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 px-3 h-9 text-xs font-semibold disabled:opacity-50">
                <Send className="h-3.5 w-3.5" /> Send mail…
            </button>
            <ExportMenu body={{ lead_ids: selectedIds }} dark label="Eksportér valgte" testId="crm-bulk-export" />
            <div className="flex-1" />
            <button
                type="button"
                onClick={onClear}
                data-testid="crm-bulk-clear"
                className="inline-flex items-center gap-1 rounded-lg text-white/70 hover:text-white px-2 h-9 text-xs">
                Ryd valg
            </button>
        </div>
    );
}

function BulkSendModal({ count, templates, onClose, onConfirm }) {
    const [selectedId, setSelectedId] = useState(templates[0]?.id || "");
    const [sending, setSending] = useState(false);
    const selected = templates.find((t) => t.id === selectedId);
    const confirm = async () => {
        if (!selected) return;
        if (!window.confirm(
            `Send "${selected.name}" til ${count} valgte lead${count === 1 ? "" : "s"}?\n\n` +
            `Leads uden samtykke, afmeldte og rate-limitede springes over.`
        )) return;
        setSending(true);
        try { await onConfirm(selected); } finally { setSending(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="crm-bulk-send-modal">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                    <div>
                        <h2 className="font-display text-xl font-bold text-slate-900">
                            Send mail til {count} lead{count === 1 ? "" : "s"}
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Vælg skabelon — GDPR-check og rate-limits håndhæves pr. lead
                        </p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
                        <XCircle className="h-5 w-5" />
                    </button>
                </div>
                <div className="grid grid-cols-2 flex-1 min-h-0">
                    <div className="border-r border-slate-100 overflow-y-auto">
                        {templates.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setSelectedId(t.id)}
                                data-testid={`crm-bulk-tpl-${t.id}`}
                                className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-b-0 transition-colors ${
                                    selectedId === t.id ? "bg-sky-50" : "hover:bg-slate-50"
                                }`}>
                                <div className="font-semibold text-sm text-slate-900 truncate">{t.name}</div>
                                <div className="text-xs text-slate-500 truncate mt-0.5">{t.subject}</div>
                            </button>
                        ))}
                    </div>
                    <div className="bg-slate-50/50 overflow-y-auto p-4">
                        {selected ? (
                            <>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Emne</div>
                                <div className="text-sm font-semibold text-slate-900 mb-4">{selected.subject}</div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Indhold</div>
                                <div
                                    className="text-xs text-slate-700 prose prose-xs max-w-none [&_p]:my-1"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selected.html) }}
                                />
                            </>
                        ) : (
                            <div className="text-sm text-slate-400 italic">Vælg en skabelon for at se forhåndsvisning</div>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100">
                    <button onClick={onClose}
                        className="px-4 h-10 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
                        Annuller
                    </button>
                    <button
                        onClick={confirm}
                        disabled={sending || !selected}
                        data-testid="crm-bulk-send-confirm"
                        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10 disabled:opacity-50">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send til {count}
                    </button>
                </div>
            </div>
        </div>
    );
}

function KpiRow({ stats, coldCount = 0 }) {
    // Resolve won/lost stage names from the /stats payload so KPI labels
    // stay in sync when a super_admin renames a system stage. Fall back
    // to Danish defaults if the API is old.
    const wonName = stats.won_stage_name || "Vundet";
    const lostName = stats.lost_stage_name || "Tabt";
    const items = [
        { icon: Users, label: "Total leads", value: stats.total_leads, tone: "slate" },
        { icon: TrendingUp, label: "Pipeline-værdi", value: fmtDkk(stats.pipeline_value_dkk), tone: "sky" },
        { icon: Snowflake, label: "Kolde leads", value: coldCount, tone: coldCount > 0 ? "rose" : "slate", warn: coldCount > 0 },
        { icon: MailIcon, label: "Åbnede", value: stats.opened_count ?? 0, tone: "sky" },
        { icon: MailIcon, label: "Besvarede", value: stats.replied_count, tone: "emerald" },
        { icon: PhoneCall, label: "Ringe klar", value: stats.by_stage["Ringe klar"] || 0, tone: "amber" },
        { icon: Calendar, label: "Bookede", value: stats.by_stage["Gennemgang arrangeret/booket"] || 0, tone: "violet" },
        { icon: Trophy, label: wonName, value: stats.won_count, tone: "emerald" },
        { icon: XCircle, label: lostName, value: stats.lost_count, tone: "rose" },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="crm-kpis">
            {items.map((it, i) => (
                <div key={i}
                    data-testid={`crm-kpi-${it.label.toLowerCase().replace(/\s+/g,'-')}`}
                    className={`rounded-xl border p-4 shadow-sm ${
                        it.warn ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"
                    }`}>
                    <div className={`flex items-center gap-2 text-xs font-medium ${
                        it.warn ? "text-rose-700" : "text-slate-500"
                    }`}>
                        <it.icon className="h-3.5 w-3.5" /> {it.label}
                    </div>
                    <div className={`mt-2 font-display text-xl font-bold ${
                        it.warn ? "text-rose-800" : "text-slate-900"
                    }`}>{it.value}</div>
                </div>
            ))}
        </div>
    );
}

function StageColumn({ stage, metrics, leads, onOpenLead, onQuickAdd, templates, onSendTemplate, selectedIds, onToggleSelect }) {
    const { isOver, setNodeRef } = useDroppable({ id: stage.name });
    const total = leads.reduce((sum, l) => sum + (l.value_dkk || 0), 0);
    const color = stage.color || "#64748B";
    const isTerminal = stage.system_key === "won" || stage.system_key === "lost";
    return (
        <div className="w-72 flex-shrink-0 flex flex-col" data-testid={`crm-stage-col-${stage.name}`}>
            {/* Sticky header — stays pinned to the top of the Kanban
                viewport while the rep scrolls down through many leads.
                `top-0` anchors to the scrollable ancestor (the Kanban
                overflow-auto container). Solid rgb() background from
                tint() means cards below won't bleed through. */}
            <div className="sticky top-0 z-20 rounded-lg px-3 py-2.5 mb-3 border shadow-sm"
                 style={{
                     backgroundColor: tint(color, 0.85),
                     borderColor: tint(color, 0.5),
                     color: color,
                 }}>
                <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: color }} />
                    <div className="font-semibold text-sm truncate flex-1">{stage.name}</div>
                    <div className="text-xs font-semibold flex-shrink-0">{leads.length}</div>
                </div>
                <div className="text-xs opacity-80 mt-0.5">{fmtDkk(total)}</div>
                {metrics && metrics.lifetime_count > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t border-current border-opacity-20 flex items-center gap-2 text-[10px] font-medium">
                        <span title="Gennemsnitlig tid som lead er i denne stage">
                            ⌀ {fmtDwell(metrics.avg_hours_in_stage)}
                        </span>
                        {!isTerminal && (
                            <span title="Andel af leads der har været i denne stage OG endte som Vundet"
                                  className="ml-auto">
                                {(metrics.conversion_rate * 100).toFixed(0)}% win
                            </span>
                        )}
                    </div>
                )}
            </div>
            <div
                ref={setNodeRef}
                data-testid={`crm-column-${stage.name}`}
                className="flex-1 min-h-[70vh] rounded-lg p-1 transition-colors"
                style={{
                    backgroundColor: isOver ? tint(color, 0.7) : "transparent",
                    boxShadow: isOver ? `inset 0 0 0 2px ${color}` : "none",
                }}
            >
                {leads.map((lead) => (
                    <DraggableLead key={lead.id} lead={lead}
                        onClick={() => onOpenLead(lead.id)}
                        templates={templates}
                        onSendTemplate={onSendTemplate}
                        selected={selectedIds?.includes(lead.id)}
                        onToggleSelect={onToggleSelect} />
                ))}
                {leads.length === 0 && (
                    <button
                        type="button"
                        onClick={onQuickAdd}
                        className="w-full text-xs text-slate-400 hover:text-slate-600 py-2 border-2 border-dashed border-slate-200 rounded-lg mt-1"
                    >
                        + Tilføj lead
                    </button>
                )}
            </div>
        </div>
    );
}

function DraggableLead({ lead, onClick, templates, onSendTemplate, selected, onToggleSelect }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: lead.id });
    // dnd-kit's PointerSensor activation distance (5px) means small pointer
    // moves count as clicks — so we can safely attach listeners to the whole
    // card and still open the drawer on plain clicks.
    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={{ opacity: isDragging ? 0.4 : 1, touchAction: "none" }}
            className="mb-2"
        >
            <LeadCard lead={lead} onClick={onClick}
                templates={templates} onSendTemplate={onSendTemplate}
                selected={selected} onToggleSelect={onToggleSelect} />
        </div>
    );
}

function LeadCard({ lead, onClick, dragging, templates, onSendTemplate, selected, onToggleSelect }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerActs, setPickerActs] = useState([]);
    const canSendMail = onSendTemplate && lead.consent && !lead.unsubscribe_at
        && templates && templates.length > 0;
    const dwell = getDwellInfo(lead);
    const isCold = dwell?.tier === "cold";
    const isWarm = dwell?.tier === "warm";

    const openPicker = async (e) => {
        e.stopPropagation();
        // Toggle: if already open, close.
        if (pickerOpen) { setPickerOpen(false); return; }
        setPickerOpen(true);
        // Lazy-fetch activities so the picker can show "sendt X" badges +
        // hover-preview. Fire-and-forget — the picker renders immediately
        // and refines once the list arrives.
        try {
            const acts = await api.listActivities(lead.id);
            setPickerActs(acts);
        } catch {
            // Silent — picker still works without sent-summary.
        }
    };

    return (
        <div
            className={`relative bg-white rounded-lg border p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing ${
                dragging ? "border-sky-400 shadow-xl rotate-1"
                : selected ? "border-sky-500 ring-2 ring-sky-200"
                : isCold ? "border-rose-300 ring-1 ring-rose-200"
                : isWarm ? "border-amber-200"
                : "border-slate-200"
            }`}
            onClick={onClick}
            data-testid={`crm-lead-card-${lead.id}`}
        >
            <div className="flex items-start gap-2">
                {onToggleSelect && (
                    <input
                        type="checkbox"
                        checked={!!selected}
                        onChange={(e) => onToggleSelect(lead.id, e.target.checked)}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`crm-card-select-${lead.id}`}
                        aria-label={`Vælg ${lead.company}`}
                        className="mt-0.5 flex-shrink-0 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                    />
                )}
                <div className="text-slate-400 mt-0.5 flex-shrink-0" aria-hidden="true">
                    <GripVertical className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-1.5">
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-slate-900 truncate">{lead.company}</div>
                            <div className="text-xs text-slate-500 truncate">{lead.contact_name}</div>
                        </div>
                        {canSendMail && (
                            <div className="relative flex-shrink-0">
                                <button
                                    type="button"
                                    title="Send mail-skabelon"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={openPicker}
                                    data-testid={`crm-card-send-mail-${lead.id}`}
                                    className={`inline-flex items-center justify-center h-6 w-6 rounded-md transition-colors ${
                                        pickerOpen ? "bg-sky-600 text-white"
                                        : "bg-sky-100 hover:bg-sky-500 text-sky-700 hover:text-white"
                                    }`}
                                >
                                    <Send className="h-3.5 w-3.5" />
                                </button>
                                {pickerOpen && (
                                    <TemplatePicker
                                        templates={templates}
                                        activities={pickerActs}
                                        onClose={() => setPickerOpen(false)}
                                        onPick={(t) => { setPickerOpen(false); onSendTemplate(lead, t); }}
                                        className="absolute right-0 top-7 z-30"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="text-xs text-slate-500 truncate flex-1">{lead.email}</div>
                        <div className="text-xs font-semibold text-sky-700 flex-shrink-0">{fmtDkk(lead.value_dkk)}</div>
                    </div>
                    {lead.phone && (
                        <div className="mt-1 text-xs text-slate-400 flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {lead.phone}
                        </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1 items-center">
                        {dwell && (
                            <span
                                data-testid={`crm-dwell-${dwell.tier}`}
                                title={
                                    dwell.tier === "cold" ? `Kold lead — ${dwell.days} dage i denne pipeline-status uden bevægelse`
                                    : dwell.tier === "warm" ? `Bliver kold snart — ${dwell.days} dage i denne pipeline-status`
                                    : "Tid i denne pipeline-status"
                                }
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 ${
                                    dwell.tier === "cold" ? "bg-rose-100 text-rose-700"
                                    : dwell.tier === "warm" ? "bg-amber-100 text-amber-700"
                                    : "bg-slate-100 text-slate-600"
                                }`}>
                                {dwell.tier === "cold" ? <Snowflake className="h-3 w-3" />
                                 : dwell.tier === "warm" ? <ThermometerSun className="h-3 w-3" />
                                 : <Clock className="h-3 w-3" />}
                                {dwell.label}
                                {dwell.tier === "cold" && <span className="ml-0.5 font-bold uppercase tracking-wider">· Kold</span>}
                                {dwell.tier === "warm" && <span className="ml-0.5 font-bold uppercase tracking-wider">· Snart kold</span>}
                            </span>
                        )}
                        {lead.last_email_at && !lead.replied_at && !lead.last_email_opened_at && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-violet-100 text-violet-700 rounded px-1.5 py-0.5"
                                title={`Mail sendt ${new Date(lead.last_email_at).toLocaleString("da-DK",{dateStyle:"short",timeStyle:"short"})}`}>
                                <MailCheck className="h-3 w-3" /> Sendt
                            </span>
                        )}
                        {lead.replied_at && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">
                                ✓ Svaret
                            </span>
                        )}
                        {lead.last_email_opened_at && !lead.replied_at && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-sky-100 text-sky-700 rounded px-1.5 py-0.5">
                                Åbnet
                            </span>
                        )}
                        {lead.needs_manual_followup && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">
                                Opfølgning
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
